import React, { useCallback, useMemo } from 'react'
import { type MessageInput, Role } from '../../../../__codegen__/api'
import { useLocalForageKey } from '../../../local-storage/useLocalForageKey.hooks'
import { type ConversationHistoryItem } from './conversationHistory.types'

const CONVERSATION_HISTORY_KEY = 'aiAgent.conversationHistory'
const MAX_CONVERSATIONS = 50 // Limit to prevent storage bloat

export const useConversationHistory = () => {
  const {
    data: conversations = [],
    setData: setConversations,
    loading,
  } = useLocalForageKey<ConversationHistoryItem[]>(CONVERSATION_HISTORY_KEY)

  // Use a ref to store current conversations to avoid dependency issues
  const conversationsRef = React.useRef<ConversationHistoryItem[]>([])

  // Update ref whenever conversations change
  React.useEffect(() => {
    conversationsRef.current = conversations || []
  }, [conversations])

  const generateConversationTitle = useCallback(
    (messages: MessageInput[], summary?: string | null): string => {
      // Use the AI-generated summary as the title if available
      if (summary && summary.trim()) {
        // Truncate to a reasonable length for display
        const trimmedSummary = summary.trim()
        if (trimmedSummary.length <= 50) {
          return trimmedSummary
        }
        return trimmedSummary.substring(0, 47) + '...'
      }

      // Fallback to first user message if no summary
      const firstUserMessage = messages.find(msg => msg.role === Role.User)
      if (!firstUserMessage?.content) {
        // This should never happen since we only save conversations with messages
        throw new Error('Cannot generate title for conversation without user messages')
      }

      // Truncate to a reasonable length for display
      const content = firstUserMessage.content.trim()
      if (content.length <= 50) {
        return content
      }

      return content.substring(0, 47) + '...'
    },
    []
  )

  const saveConversation = useCallback(
    (conversationId: string, messages: MessageInput[], summary?: string | null) => {
      if (messages.length === 0) {
        return // Don't save empty conversations
      }

      const now = new Date().toISOString()
      const title = generateConversationTitle(messages, summary)

      // Check if conversation already exists to preserve createdAt
      const existingConversation = conversationsRef.current.find(conv => conv.id === conversationId)

      const newConversation: ConversationHistoryItem = {
        id: conversationId,
        title,
        summary: summary || null,
        messages,
        createdAt: existingConversation?.createdAt || now,
        updatedAt: now,
      }

      const currentConversations = conversationsRef.current
      const filtered = currentConversations.filter(conv => conv.id !== conversationId)
      const updated = [newConversation, ...filtered]
      // Keep only the most recent conversations
      setConversations(updated.slice(0, MAX_CONVERSATIONS))
    },
    [generateConversationTitle, setConversations]
  )

  const loadConversation = useCallback(
    (conversationId: string): ConversationHistoryItem | null =>
      conversationsRef.current.find(conv => conv.id === conversationId) || null,
    []
  )

  const deleteConversation = useCallback(
    (conversationId: string) => {
      const currentConversations = conversationsRef.current
      const filtered = currentConversations.filter(conv => conv.id !== conversationId)
      setConversations(filtered)
    },
    [setConversations]
  )

  const clearAllConversations = useCallback(() => {
    setConversations([])
  }, [setConversations])

  const sortedConversations = useMemo(
    () =>
      [...conversations].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      ),
    [conversations]
  )

  return {
    conversations: sortedConversations,
    saveConversation,
    loadConversation,
    deleteConversation,
    clearAllConversations,
    generateConversationTitle,
    loading,
  }
}
